import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useAdminContext } from "@/contexts/AdminContext";
import { hasAccess } from "@/lib/tierGating";

interface NwcContextValue {
  /** Whether NWC mode is currently active */
  nwcEnabled: boolean;
  /** Toggle NWC on/off — respects tier gating */
  setNwcEnabled: (v: boolean) => void;
  /** Whether the current user can access NWC features */
  canAccessNwc: boolean;
}

const NwcContext = createContext<NwcContextValue>({
  nwcEnabled: false,
  setNwcEnabled: () => {},
  canAccessNwc: false,
});

const STORAGE_KEY = "seatech_nwc_enabled";

export function NwcProvider({ children }: { children: ReactNode }) {
  const { profile } = useProfile();
  const { adminMode } = useAdminContext();
  const canAccessNwc = hasAccess(profile?.tier, "pro", adminMode);

  const [nwcEnabled, setNwcEnabledRaw] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(nwcEnabled));
    } catch {}
  }, [nwcEnabled]);

  // If user loses access, disable NWC
  useEffect(() => {
    if (!canAccessNwc && nwcEnabled) setNwcEnabledRaw(false);
  }, [canAccessNwc, nwcEnabled]);

  const setNwcEnabled = (v: boolean) => {
    if (v && !canAccessNwc) return;
    setNwcEnabledRaw(v);
  };

  return (
    <NwcContext.Provider value={{ nwcEnabled, setNwcEnabled, canAccessNwc }}>
      {children}
    </NwcContext.Provider>
  );
}

export const useNwc = () => useContext(NwcContext);
